import React, { useState, useEffect } from 'react';
import { currenciesApi } from '../lib/api';
import { Coins, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const CurrencyConfigPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', decimal_places: 2, display_symbol: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await currenciesApi.list(); setCurrencies(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await currenciesApi.add({ ...form, decimal_places: parseInt(form.decimal_places) || 2 });
      toast.success('Currency added');
      setShowModal(false);
      setForm({ code: '', name: '', decimal_places: 2, display_symbol: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add currency'); }
  };

  const handleRemove = async (code) => {
    if (!window.confirm(`Remove ${code}?`)) return;
    try { await currenciesApi.remove(code); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="currency-config-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Organization</span><span>/</span><span className="text-[#1f1f1f] font-medium">Currency Configuration</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Currency Configuration</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-currency-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Currency
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Code</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Symbol</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Decimal Places</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            currencies.map(c => (
              <tr key={c.code} className="border-b hover:bg-gray-50" data-testid={`currency-row-${c.code}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{c.code}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.display_symbol}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.decimal_places}</td>
                <td className="px-4 py-3">{c.code !== 'ZAR' ? <Button variant="ghost" size="sm" onClick={() => handleRemove(c.code)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button> : <span className="text-xs text-gray-400">Base</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="currency-form-desc">
          <DialogHeader><DialogTitle>Add Currency</DialogTitle></DialogHeader>
          <p id="currency-form-desc" className="sr-only">Form to add a currency</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="e.g. USD" maxLength={3} data-testid="currency-code" /></div>
              <div><Label>Symbol *</Label><Input value={form.display_symbol} onChange={e => setForm({...form, display_symbol: e.target.value})} placeholder="e.g. $" data-testid="currency-symbol" /></div>
            </div>
            <div><Label>Currency Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. US Dollar" data-testid="currency-name" /></div>
            <div><Label>Decimal Places</Label><Input type="number" value={form.decimal_places} onChange={e => setForm({...form, decimal_places: e.target.value})} data-testid="currency-decimals" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="currency-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrencyConfigPage;
